import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Send, Upload, FileText, CheckCircle2, CircleDot, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { portal } from "../../lib/api";
import { PortalShell } from "./PortalHome";

const SEVERITY_STYLES = {
  high: "text-red-700", medium: "text-amber-700", normal: "text-slate-600", low: "text-slate-500",
};

export default function PortalTransaction() {
  const { id } = useParams();
  const [tx, setTx] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [uploading, setUploading] = useState(false);
  const [askQ, setAskQ] = useState("");
  const [askA, setAskA] = useState(null);
  const [asking, setAsking] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [t, m] = await Promise.all([
      portal.get(`/portal/transactions/${id}`),
      portal.get(`/portal/transactions/${id}/messages`),
    ]);
    setTx(t.data); setMsgs(m.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  // Keep the transaction view in sync with server truth. When the agent
  // deletes a doc from Transactions/Client Detail, we need this page —
  // which the client may be sitting on — to drop the stale row without
  // requiring a hard refresh. Three triggers:
  //   1. 15s poll while the tab is visible
  //   2. visibilitychange → refetch the moment the client tabs back
  //   3. window focus → covers browsers that don't fire visibilitychange
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (document.hidden) return;
      load().catch(() => {});
    };
    const timer = setInterval(refresh, 15_000);
    const onVis = () => { if (!document.hidden) refresh(); };
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true; // eslint-disable-line no-unused-vars
      clearInterval(timer);
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await portal.post(`/portal/transactions/${id}/messages`, { body: body.trim() });
      setBody(""); load();
    } catch { toast.error("Send failed"); }
  };

  const upload = async (f) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await portal.post(`/portal/transactions/${id}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded"); load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const ask = async (e) => {
    e.preventDefault();
    const q = askQ.trim();
    if (q.length < 3) return;
    setAsking(true);
    setAskA(null);
    try {
      const { data } = await portal.post(`/portal/transactions/${id}/ask`, { question: q });
      setAskA(data);
    } catch { toast.error("Ask failed — try again"); }
    finally { setAsking(false); }
  };

  const quickAsk = (q) => { setAskQ(q); setTimeout(() => document.querySelector('[data-testid="portal-ask-submit"]')?.click(), 0); };

  if (!tx) return <PortalShell><div className="text-sm text-slate-500">Loading…</div></PortalShell>;

  const totalDeadlines = tx.deadlines.length;
  const met = tx.deadlines.filter(d => d.status === "met" || d.status === "waived").length;
  const progress = totalDeadlines ? Math.round((met / totalDeadlines) * 100) : 0;

  return (
    <PortalShell>
      <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4" /> Back</Link>

      <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6">
        <h1 className="font-heading text-2xl font-bold tracking-tighter text-slate-900" data-testid="portal-tx-address">{tx.property_address || "—"}</h1>
        <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">{tx.side} · {tx.status.replace(/_/g," ")}</div>

        <div className="mt-6">
          <div className="flex items-baseline justify-between text-xs text-slate-500 mb-1"><span>Progress</span><span className="tabular-nums">{met} of {totalDeadlines} milestones</span></div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "var(--primary-hex)" }} /></div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-6 text-sm">
          {tx.purchase_price && <div><div className="text-xs uppercase tracking-widest text-slate-500">Price</div><div className="mt-1 font-heading font-bold text-slate-900 tabular-nums">${tx.purchase_price.toLocaleString()}</div></div>}
          {tx.acceptance_date && <div><div className="text-xs uppercase tracking-widest text-slate-500">Accepted</div><div className="mt-1 font-heading font-bold text-slate-900 tabular-nums">{tx.acceptance_date}</div></div>}
          {tx.close_of_escrow_date && <div><div className="text-xs uppercase tracking-widest text-slate-500">Close of escrow</div><div className="mt-1 font-heading font-bold text-slate-900 tabular-nums">{tx.close_of_escrow_date}</div></div>}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6" data-testid="portal-ask-widget">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-slate-500" />
          <h2 className="font-heading text-sm font-bold tracking-tight text-slate-900">Ask about my transaction</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">Instant answers pulled from your deadlines, documents, and messages. Your agent still sees everything.</p>

        <form onSubmit={ask} className="flex items-center gap-2">
          <input
            value={askQ}
            onChange={e => setAskQ(e.target.value)}
            placeholder="e.g. When is my inspection deadline?"
            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand"
            data-testid="portal-ask-input"
          />
          <button
            type="submit"
            disabled={asking || askQ.trim().length < 3}
            className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--primary-hex)" }}
            data-testid="portal-ask-submit"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {asking ? "Thinking…" : "Ask"}
          </button>
        </form>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {["What's next?", "When do I need to move earnest money?", "Have all my documents been received?"].map(q => (
            <button
              key={q}
              type="button"
              onClick={() => quickAsk(q)}
              className="text-[11px] rounded-full border border-slate-200 bg-slate-50 hover:bg-white px-2.5 py-1 text-slate-600"
              data-testid={`portal-ask-quick-${q.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g,'')}`}
            >
              {q}
            </button>
          ))}
        </div>

        {askA && (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid="portal-ask-answer">
            <div className="text-sm text-slate-900 whitespace-pre-wrap">{askA.answer}</div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500">
              Grounded in {askA.context_used.deadlines} deadline{askA.context_used.deadlines === 1 ? "" : "s"} · {askA.context_used.documents} doc{askA.context_used.documents === 1 ? "" : "s"} · {askA.context_used.recent_messages} recent message{askA.context_used.recent_messages === 1 ? "" : "s"}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200"><h2 className="font-heading font-bold text-sm text-slate-900">Milestones</h2></div>
          <ul className="divide-y divide-slate-100">
            {tx.deadlines.map(d => {
              const done = d.status === "met" || d.status === "waived";
              return (
                <li key={d.id} className="px-5 py-3 flex items-center gap-3" data-testid={`portal-deadline-${d.id}`}>
                  {done ? <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "var(--primary-hex)" }} /> : <CircleDot className="h-4 w-4 text-slate-300 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${done ? "text-slate-400 line-through" : "text-slate-900 font-medium"}`}>{d.label}</div>
                  </div>
                  <span className={`text-xs tabular-nums ${SEVERITY_STYLES[d.severity]}`}>{d.due_date}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div id="chat" className="rounded-lg border border-slate-200 bg-white flex flex-col scroll-mt-20" style={{ height: "500px" }}>
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-heading font-bold text-sm text-slate-900">Message your agent</h2>
            <span className="text-[10px] uppercase tracking-widest text-slate-500">In-app · no SMS</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="portal-msg-body">
            {msgs.length === 0 && <div className="text-sm text-slate-500 text-center py-8">Say hi — your agent will see it here.</div>}
            {msgs.map(m => (
              <div key={m.id} className={`max-w-sm rounded-lg px-3 py-2 text-sm ${m.sender === "client" ? "ml-auto text-white" : "bg-slate-100 text-slate-900"}`} style={m.sender === "client" ? { background: "var(--primary-hex)" } : {}}>
                <div>{m.body}</div>
                <div className={`text-[10px] mt-1 ${m.sender === "client" ? "opacity-80" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-3 flex items-center gap-2">
            <input value={body} onChange={e => setBody(e.target.value)} placeholder="Type a message…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="portal-msg-input" />
            <button type="submit" className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="portal-msg-send"><Send className="h-3.5 w-3.5" /></button>
          </form>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading font-bold text-sm text-slate-900">Documents</h2>
          <label className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold text-white cursor-pointer" style={{ background: "var(--primary-hex)" }} data-testid="portal-upload-btn">
            <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload"}
            <input ref={fileRef} type="file" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} data-testid="portal-upload-input" />
          </label>
        </div>
        <ul className="divide-y divide-slate-100">
          {tx.documents.length === 0 && <li className="px-5 py-6 text-center text-sm text-slate-500" data-testid="portal-docs-empty">No documents yet.</li>}
          {tx.documents.map(d => (
            <li key={d.id} className="px-5 py-3 flex items-center gap-3 text-sm">
              <FileText className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0 truncate text-slate-900">{d.name}</div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{d.uploaded_by_agent ? "from agent" : "from you"}</span>
            </li>
          ))}
        </ul>
      </div>
    </PortalShell>
  );
}
